import { Tooltip } from "@mui/material";
import { GridRenderCellParams, GridRowId } from "@mui/x-data-grid-premium";
import { DestinationViewType } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";
import { formatAmountInPounds } from "util/formatNumber";

import {
  EMANIFEST_OVERRIDE_EXPLANATION,
  formatStreamEManifestAmountWithOverrides,
} from "../../../../eManifestAmountOverrideText";

const renderDestinationEManifestAmount = ({ row }: GridRenderCellParams) => {
  const withOverrides = formatStreamEManifestAmountWithOverrides(
    row.eManifestAmount,
    row.eManifestAmountWithOverride
  );
  if (!withOverrides) return formatAmountInPounds(row.eManifestAmount);
  return (
    <Tooltip title={EMANIFEST_OVERRIDE_EXPLANATION}>
      <span>{withOverrides}</span>
    </Tooltip>
  );
};

export const useDestinationsGrid = (viewType: DestinationViewType) => {
  const columns: OmnisearchGridColDef<any>[] = useMemo(() => {
    switch (viewType) {
      case DestinationViewType.Destinations:
        return [
          {
            field: "destinationFacilityId",
            headerName: "Destination Site ID",
            flex: 1,
            minWidth: 150,
          },
          {
            field: "destinationFacilityName",
            headerName: "Destination Site Name",
            flex: 1.5,
            minWidth: 200,
          },
          {
            field: "managementMethodCode",
            headerName: "Management Method Code",
            flex: 0.8,
            minWidth: 120,
          },
          {
            field: "vendorAmount",
            headerName: "Amount (Vendor)",
            filterKeyType: "number",
            flex: 0.8,
            minWidth: 120,
            valueFormatter: (value) => formatAmountInPounds(value),
          },
          {
            field: "eManifestAmount",
            headerName: "Amount (e‑Manifest)",
            filterKeyType: "number",
            flex: 0.8,
            minWidth: 200,
            renderCell: renderDestinationEManifestAmount,
          },
        ];

      case DestinationViewType.ManagementMethods:
        return [
          {
            field: "managementMethodCode",
            headerName: "Management Method Code",
            flex: 1,
            minWidth: 200,
          },
          {
            field: "vendorAmount",
            headerName: "Amount (Vendor)",
            filterKeyType: "number",
            flex: 1,
            minWidth: 150,
            valueFormatter: (value) => formatAmountInPounds(value),
          },
          {
            field: "eManifestAmount",
            headerName: "Amount (e‑Manifest)",
            filterKeyType: "number",
            flex: 1,
            minWidth: 200,
            renderCell: renderDestinationEManifestAmount,
          },
        ];

      case DestinationViewType.DestinationsByManagementMethod:
        return [
          {
            field: "managementMethodCode",
            headerName: "Management Method Code",
            flex: 1,
            minWidth: 150,
          },
          {
            field: "destinationFacilityId",
            headerName: "Destination Site ID",
            flex: 1,
            minWidth: 150,
          },
          {
            field: "destinationFacilityName",
            headerName: "Destination Site Name",
            flex: 1.5,
            minWidth: 200,
          },
          {
            field: "vendorAmount",
            headerName: "Amount (Vendor)",
            filterKeyType: "number",
            flex: 0.8,
            minWidth: 120,
            valueFormatter: (value) => formatAmountInPounds(value),
          },
          {
            field: "eManifestAmount",
            headerName: "Amount (e‑Manifest)",
            filterKeyType: "number",
            flex: 0.8,
            minWidth: 200,
            renderCell: renderDestinationEManifestAmount,
          },
        ];

      default:
        return [];
    }
  }, [viewType]);

  const getRowId = useCallback(
    (row: any): GridRowId =>
      viewType === DestinationViewType.ManagementMethods
        ? `${row.managementMethodCode}-${row.destinationFacilityName}`
        : viewType === DestinationViewType.DestinationsByManagementMethod
          ? `${row.destinationFacilityId}-${row.managementMethodCode}-${row.destinationFacilityName}`
          : `${row.destinationFacilityId}-${row.destinationFacilityName}`,
    [viewType]
  );

  return useMemo(
    () => ({
      columns,
      getRowId,
    }),
    [columns, getRowId]
  );
};
